
// @NA 2025-09-22: Separate Evidence and Retail gallery pages

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../contexts/TranslationContext";
import { useGlobalPopup } from "../../contexts/GlobalPopupContext";
import api from '../../utils/api';

import PageTitle from "../UC/PageTitle";
import Page from "../UC/Page";

import RetailFilter from "./RetailFilter";
import GalleryCard from "./GalleryCard";
import GalleryList from "./GalleryList";
import Details from "../Modals/Details";
import Import from "./Import";
import Pagination from "../UC/Pagination";
import ViewStyle from "../UC/ViewStyle";
import Loader from "../UC/Loader";
import NoRecordFound from "../UC/NoRecordFound";

import "../../assets/css/retail.scss";

const Retail = ({ selectionMode, onClose, onSelect }) => {
    const navigate = useNavigate();
    const dialogRef = useRef(null);
    const editDialogRef = useRef(null);
    const abortControllerRef = useRef(null);
    const { showDeleteConfirm, showError, showSuccess, showWarning, showConfirm } = useGlobalPopup();
    const { language } = useTranslation();

    const svgIconRetail = (
        <svg viewBox="0 -960 960 960" fill="#2D68F0" style={{border: '1px solid #2D68F0', borderRadius: '4px', padding: '2px'}}>
            {/* flip the object */}
            <g transform="scale(-1, 1) translate(-960, 0)">
            <path d="M216-580q39 0 74 14t64 41l382 365h24q17 0 28.5-11.5T800-200q0-8-1.5-17T788-235L605-418l-71-214-74 18q-38 10-69-14t-31-63v-84l-28-14-154 206q-1 1-1 1.5t-1 1.5h40Zm0 80h-46q3 7 7.5 13t10.5 11l324 295q11 11 25 16t29 5h54L299-467q-17-17-38.5-25t-44.5-8ZM566-80q-30 0-57-11t-50-31L134-417q-46-42-51.5-103T114-631l154-206q17-23 45.5-30.5T368-861l28 14q21 11 32.5 30t11.5 42v84l74-19q30-8 58 7.5t38 44.5l65 196 170 170q20 20 27.5 43t7.5 49q0 50-35 85t-85 35H566Z" />
            </g>
        </svg>
    );

    // Check if current user has permission to edit/delete a record
    const checkUserPermission = (record) => {
        try {
            const userData = localStorage.getItem("FWUser");
            if (!userData) {
                return false;
            }
            const parsedUser = JSON.parse(userData);
            const currentUserId = parsedUser.Information?.User_ID || parsedUser.User_ID;

            return currentUserId.toString() === record.User_ID.toString(); // Ensure both are strings for comparison
        } catch (error) {
            console.error("Error checking user permission:", error);
            return false;
        }
    };

    const defaultFilterCatalog = useMemo(() => ({
        Make_IDs: [],
        ShoeTypes: [],
        Models: [],
        ProductCode: "",
    }), []);

    const [selectedRecord, setSelectedRecord] = useState();
    const [editRecord, setEditRecord] = useState(null);
    const [selectedId, setSelectedId] = useState('0');
    const [viewMode, setViewMode] = useState('grid');
    const [filters, setFilters] = useState(null);
    const [galleries, setGalleries] = useState([]);
    const [resultSummary, setResultSummary] = useState(0);
    const [arrRelatedAccounts, setArrRelatedAccounts] = useState([]);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [isSearching, setIsSearching] = useState(false);
    const [isFilterReady, setIsFilterReady] = useState(false);
    const [isResettingFilters, setIsResettingFilters] = useState(false);

    const handleEditGallery = (record) => {
        // Check if current user has permission to edit this record
        if (!checkUserPermission(record)) {
            showWarning(
                language.gallery?.noPermission || 'No Permission',
                language.gallery?.noEditPermission || 'You do not have permission to edit this item. You can only edit items you created.'
            );
            return;
        }

        console.log("Editing gallery:", record);
        setEditRecord(record);
        if (editDialogRef.current && editDialogRef.current.showModal) {
            editDialogRef.current.showModal();
        }
    };

    const handleCloseEditModal = () => {
        setEditRecord(null);
        if (editDialogRef.current && editDialogRef.current.close) {
            editDialogRef.current.close();
        }
    };

    const handleEditSuccess = () => {
        // Only refresh gallery data when edit is actually successful
        console.log("Edit successful, refreshing gallery data");
        searchGallery(pageIndex, pageSize, filters);
    };

    const handleDeleteGallery = (record) => {
        // Check if current user has permission to delete this record
        if (!checkUserPermission(record)) {
            showWarning(
                language.gallery?.noPermission || 'No Permission',
                language.gallery?.noDeletePermission || 'You do not have permission to delete this item. You can only delete items you created.'
            );
            return;
        }

        let itemName = record.Make && record.Model
            ? `${record.Make} ${record.Model}`
            : language.gallery?.thisItem || 'this item';

        showDeleteConfirm(
            language.gallery?.deleteGalleryItem || 'Delete Gallery Item',
            `${language.gallery?.deleteConfirmMessage || 'Are you sure you want to delete'} "${itemName}"? ${language.gallery?.deleteActionWarning || 'This action cannot be undone.'}`,
            async () => {
                try {
                    console.log("Deleting gallery:", record);

                    // Use api.post with catalog endpoint and send IDValue as array
                    const response = await api.post(`${global.config.api.domain}/fwiq-api/api/catalog/delete`,
                        [record.IDValue] // Send IDValue as an array
                    );

                    if (response.status === 200 || response.status === 204) {
                        showSuccess(language.gallery?.success || 'Success', `"${itemName}" ${language.gallery?.deleteSuccessMessage || 'has been deleted successfully.'}`);
                        // Refresh the gallery data after delete
                        searchGallery(pageIndex, pageSize, filters);
                    } else {
                        showError(language.gallery?.deleteFailed || 'Delete Failed', language.gallery?.deleteFailedMessage || 'Failed to delete the item. Please try again.');
                    }
                } catch (error) {
                    console.error("Error deleting gallery item:", error);
                    showError(language.gallery?.deleteFailed || 'Delete Failed', language.gallery?.deleteErrorMessage || 'An error occurred while deleting the item. Please try again.');
                }
            }
        );
    };

    const handleReUpload = (record) => {
        let itemName = record.Make && record.Model
            ? `${record.Make} ${record.Model}`
            : language.gallery?.thisItem || 'this item';

        showConfirm(
            language.gallery?.reUploadConfirmTitle || 'Re-Upload Confirmation',
            `${language.gallery?.reUploadConfirmMessage || 'Are you sure you want to re-upload this item? This will process the images again.'} "${itemName}"`,
            async () => {
                try {
                    console.log("Re-uploading catalog item:", record);

                    // Use api.post with catalog reupload endpoint
                    const response = await api.post(`${global.config.api.domain}/fwiq-api/api/catalog/reupload`,
                        [record.IDValue] // Send IDValue as an array
                    );

                    if (response.status === 200 || response.status === 204) {
                        showSuccess(language.gallery?.reUploadSuccess || 'Success', `"${itemName}" ${language.gallery?.reUploadSuccessMessage || 'has been re-uploaded successfully.'}`);
                        // Refresh the gallery data after re-upload
                        searchGallery(pageIndex, pageSize, filters);
                    } else {
                        showError(language.gallery?.reUploadError || 'Re-Upload Failed', language.gallery?.reUploadErrorMessage || 'Failed to re-upload the item. Please try again.');
                    }
                } catch (error) {
                    console.error("Error re-uploading catalog item:", error);
                    showError(language.gallery?.reUploadError || 'Re-Upload Failed', language.gallery?.reUploadErrorMessage || 'Failed to re-upload the item. Please try again.');
                }
            }
        );
    };

    const searchGallery = useCallback((appliedPageIndex = pageIndex, appliedPageSize = pageSize, appliedFilters = filters) => {
        if (!appliedFilters) return;

        // Cancel previous request if it exists
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new AbortController for this request
        abortControllerRef.current = new AbortController();
        const currentController = abortControllerRef.current;

        setIsSearching(true);

        const fetchGalleryData = async () => {
            try {
                // Check if request was aborted before starting
                if (currentController.signal.aborted) {
                    console.log("Request aborted before fetch");
                    return;
                }
                
                const response = await api.post(`${global.config.api.domain}/fwiq-api/api/catalog/search`, {
                    PageNumber: appliedPageIndex,
                    PageSize: appliedPageSize,
                    ...appliedFilters,
                }, {
                    signal: currentController.signal // Add abort signal for axios
                });

                // Check if request was aborted after fetch
                if (currentController.signal.aborted) {
                    console.log("Request aborted after fetch");
                    return;
                }

                if (response.status === 200) {
                    const data = response.data;

                    // Final check before updating state
                    if (currentController.signal.aborted) {
                        console.log("Request aborted before updating state");
                        return;
                    }

                    if (data?.Records) {
                        setGalleries(data.Records);
                        setResultSummary(data.TotalRecords || 0);
                        setArrRelatedAccounts(data.RelatedAccounts || []);
                    } else {
                        setGalleries([]);
                        setResultSummary(0);
                        setArrRelatedAccounts([]);
                    }
                } else {
                    if (!currentController.signal.aborted) {
                        console.error("Failed to fetch catalog data");
                        setGalleries([]);
                        setResultSummary(0);
                        setArrRelatedAccounts([]);
                    }
                }
            } catch (error) {
                if (error.name === 'AbortError' || currentController.signal.aborted) return;
                if (error.response?.status === 401) return; // Let axiosConfig interceptor handle logout
                console.error('Error searching catalog:', error);
                setGalleries([]);
                setResultSummary(0);
                setArrRelatedAccounts([]);
            } finally {
                if (currentController && !currentController.signal.aborted) {
                    setIsSearching(false);
                }
            }
        };

        fetchGalleryData();
    }, [filters, pageIndex, pageSize]);

    // Reset to index 1 when filters change
    useEffect(() => {
        if (filters) {
            setPageIndex(1);
        }
    }, [filters]);

    useEffect(() => {
        if (selectedRecord) {
            const newSelectedId = selectedRecord?.IDValue || '0';
            setSelectedId(newSelectedId);

            // Open details modal if not in selection mode
            if (!selectionMode && dialogRef.current && dialogRef.current.showModal) {
                dialogRef.current.showModal();
            }
        } else {
            setSelectedId('0');
        }
    }, [selectedRecord, selectionMode]);

    // Initialize filters when filter is ready
    useEffect(() => {
        if (!isFilterReady) {
            setIsSearching(true);
            return;
        }

        if (!filters) {
            setFilters(defaultFilterCatalog);
        }
    }, [isFilterReady, filters, defaultFilterCatalog]);

    // Separate useEffect to handle searchGallery when filters are ready.
    // Debounce (300ms) prevents double-fire when filters change AND pageIndex
    // resets to 1 in the same tick — only the final state triggers a fetch.
    useEffect(() => {
        if (!isFilterReady || !filters) {
            if (!isFilterReady) setIsSearching(true);
            return;
        }

        const debounceTimer = setTimeout(() => {
            searchGallery(pageIndex, pageSize, filters);
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [filters, pageIndex, pageSize, searchGallery, isFilterReady]);

    // Cleanup function to abort requests on component unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const handleApplyFilters = (newFilters) => {
        setFilters(newFilters);
        setPageIndex(1);
    };

    // Callback for when RetailFilter is ready
    const handleFilterReady = useCallback((isReady) => {
        setIsFilterReady(isReady);
    }, []);

    const handleFocusRecord = (record) => {
        // Set the selected ID to highlight the card
        setSelectedId(record.IDValue);
        console.log("focused record:", record.IDValue);

        if (selectionMode) {
            onSelect(record);
        }
    };

    const handleOpenDetails = (record) => {
        if (selectionMode && onSelect) {
            // In selection mode, double-click selects the record
            setSelectedId(record.IDValue);
            console.log("selected record:", record.IDValue);
            onSelect(record);
        } else {
            // In normal mode, double-click opens details
            setSelectedRecord(record);
        }
    };

    // Handle analyze functionality - navigate to analyze page with selected record
    const handleAnalyze = (record) => {
        // Store the selected record in sessionStorage so it can be retrieved by the Analyze component
        sessionStorage.setItem('selectedAnalyzeRecord', JSON.stringify(record));
        // Navigate to analyze page
        navigate('/analyze');
    };

    // Page header content
    const pageHeaderContent = (
        <div className="header-content">
            <PageTitle
                title={language.retail?.pageTitle || "Retail Gallery"}
                subtitle={language.retail?.pageSubtitle || "View and manage retail footwear catalog"}
                icon={svgIconRetail}
            />
            <div className="header-actions">
                <ViewStyle viewMode={viewMode} setViewMode={setViewMode} />
            </div>
        </div>
    );

    // Sidebar filter content
    const sidebarFilterContent = (
        <>
            <div className="sidebar-header">
                <div className="filters-header">
                    <i className="fa fa-filter"></i>
                    <span>Filters</span>
                </div>
                <div className="filters-actions">
                    <button className="btn btn-default" onClick={() => {
                        setFilters(defaultFilterCatalog);
                        setIsResettingFilters(true);
                        setTimeout(() => {
                            setIsResettingFilters(false);
                        }, 100);
                    }}>
                        <i className="fa fa-refresh"></i>
                        {language.buttons?.resetAll || "Reset All"}
                    </button>
                </div>
            </div>

            <RetailFilter
                applyFilters={handleApplyFilters}
                onFilterReady={handleFilterReady}
                className="retail-filters"
                defaultFilters={defaultFilterCatalog}
                resetFiltersTrigger={isResettingFilters}
            />
        </>
    );

    // Search result content
    const searchResultContent = (
        <>
            <div className={`result-view ${viewMode}-view ${isSearching ? 'loading' : ''}`}>
                {isSearching ? (
                    <Loader text={language.messages?.loadingCatalog || "Loading catalog data..."} />
                ) : galleries.length === 0 ? (
                    <NoRecordFound language={language} />
                ) : viewMode === 'list' ? (
                    <GalleryList
                        galleries={galleries}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        selectedId={selectedId}
                        galleryType="catalog"
                        selectionMode={selectionMode}
                        handleFocusRecord={handleFocusRecord}
                        handleOpenDetails={handleOpenDetails}
                        handleEditGallery={handleEditGallery}
                        handleDeleteGallery={handleDeleteGallery}
                        handleReUpload={handleReUpload}
                    />
                ) : (
                    <div className={`results ${viewMode}`}>
                        {galleries.map((record, idx) => (
                            <GalleryCard
                                key={record.IDValue}
                                index={(pageIndex - 1) * pageSize + idx + 1}
                                record={record}
                                isSelected={record.IDValue === selectedId}
                                selectRecord={() => handleFocusRecord(record)}
                                viewDetails={() => handleOpenDetails(record)}
                                hideActions={selectionMode}
                                analyzeFunc={handleAnalyze}
                                editFunc={(e) => {
                                    e.stopPropagation();
                                    handleEditGallery(record);
                                }}
                                deleteFunc={(e) => {
                                    e.stopPropagation();
                                    handleDeleteGallery(record);
                                }}
                            />
                        ))}
                    </div>
                )}
            </div>

            <div className="result-summary">
                <Pagination
                    totalRecords={resultSummary || 0}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    changePageIndex={(newPageIndex) => {
                        setPageIndex(newPageIndex);
                    }}
                    changePageSize={(newPageSize) => {
                        setPageSize(newPageSize);
                        setPageIndex(1); // Reset to first page when page size changes
                    }}
                />
            </div>
        </>
    );

    return (
        <>
            <Page
                extentClassName="retail"
                pageHeaderContent={pageHeaderContent}
                sidebarFilterContent={sidebarFilterContent}
                searchResultContent={searchResultContent}
            />

            {/* Details Dialog */}
            <dialog ref={dialogRef} className="dialog">
                {selectedRecord && (
                    <Details
                        arrAccounts={arrRelatedAccounts}
                        record={selectedRecord}
                        onClose={() => {
                            if (dialogRef.current) {
                                dialogRef.current.close();
                            }
                            setSelectedRecord(null);
                        }}
                    />
                )}
            </dialog>

            {/* Edit Dialog */}
            <dialog ref={editDialogRef} className="dialog edit-dialog">
                {editRecord && (
                    <Import
                        isModal={true}
                        editId={editRecord.IDValue}
                        onClose={handleCloseEditModal}
                        onEditSuccess={handleEditSuccess}
                        arrAccounts={arrRelatedAccounts}
                    />
                )}
            </dialog>

            {/* Selection Mode Close Button */}
            {selectionMode && onClose && (
                <button className="selection-close-btn" onClick={onClose}>
                    <i className="fa fa-times"></i>
                    {language.buttons?.close || "Close"}
                </button>
            )}
        </>
    );
};

export default Retail;
